import { latchedObject } from '../janus-engine/movement/latch-link.js';
import { paintMarkup } from '../player-character-sheet/paint-markup';
// creature-action-buttons.ts — client-side render of the caster special-actions column
// + the End Concentration buttons, reading from window.FateTide.casterCache.
//
// Replaces (PHP → TS islands):
//   - render_caster_special_actions_column → renderCasterSpecialActionsInline
//       (Luck/Fortunate, End Concentration, Stop Burning, End Hunker
//        Down, Revert Shapeshift). Called on the character sheet core-abilities strip.
//   - render_end_concentration_action_buttons → renderEndConcentrationButtonsInline
//       (one End Concentration button per concentrating status; compact_toolbar variant
//        used by the castable-spells nav row).
// Both were defined in includes/functions/janus/core/creature_summary_renderers.php.
//
// Data: caster.encounter_creatures.luck, caster.shapeshifted, caster.status_effect —
// all in the cached blob. The onclick handlers (handleAbilityBtnSingleClick /
// handleuseAbilityVariable1BtnSingleClick) are emitted verbatim; they already exist on window.

import type { CachedCaster, ItemLikeRecord } from './types';

const CDN_ICONS = 'https://cdn.fatetide.com/images/icons/';

// StatusEffect ids (catalogs/StatusEffect.php).
const CONCENTRATING_ID = 'E7FDB796-26DA-E0F5-8F88-C21FE01D923B';
const BURNING_ID = '09FC38BC-6060-4B67-A33F-EF7D448F71F1';
const HUNKERED_DOWN_ID = 'BE6DC590-4EB7-B297-5AC6-8E47AA4E00EF';

// Ability ids fired by the buttons.
const FORTUNATE_ABILITY = 'AE92D0EF-4E53-4A1D-DD97-B0B2F3732BB8';
const END_CONCENTRATION_ABILITY = '5496F09C-94BB-452E-9DDD-47CC871CFB5E';
const STOP_BURNING_ABILITY = '55555C0C-D0EB-4497-8971-C7133629964F';
const END_HUNKER_ABILITY = 'CFC9BEC2-556E-E00F-7BA7-D61E8AC382D3';
const REVERT_SHAPESHIFT_ABILITY = '84DA8D8C-7B6D-444E-B76F-8B174B14A6C0';

function unwrap(v: unknown): unknown {
    return Array.isArray(v) ? v[0] : v;
}
function ecArrOf(caster: CachedCaster): Record<string, unknown> {
    const ec = caster.encounter_creatures;
    return (ec && !Array.isArray(ec)) ? (ec as Record<string, unknown>) : {};
}
function statusRows(caster: CachedCaster): unknown[] {
    const rows: unknown = ftCreatureStatuses(caster, 'target');
    return Array.isArray(rows) ? rows : [];
}
function itemField(row: unknown, field: string): string {
    if (!row || typeof row !== 'object') return '';
    const o = row as ItemLikeRecord;
    const raw = o.items?.[field] ?? o[field];
    return String(unwrap(raw) ?? '');
}
/** item_field(status_row, 'item_id') twin. Lean status rows from ftCreatureStatuses carry
 *  the catalog id under the status def (status_arr.items.item_id) or as a bare-UUID ref under
 *  status_item_id — NEVER at the row's own items.item_id / item_id (that always reads ''). This
 *  mirrors the PHP item_field() status-entry identity bridge (build_item_arr.php). Without it,
 *  hasStatus() and the End-Concentration filter never matched and the status-driven buttons
 *  (End Concentration, Stop Burning, Exit Cover, End Hunker Down) silently never rendered. */
function statusItemId(row: unknown): string {
    if (!row || typeof row !== 'object' || Array.isArray(row)) return '';
    const record = row as Record<string, unknown>;
    const sa = record.status_arr ?? record.status_item_id;
    if (sa && typeof sa === 'object') {
        const status = sa as ItemLikeRecord;
        return String(unwrap(status.items?.item_id ?? status.item_id) ?? '');
    }
    if (typeof sa === 'string' && sa !== '') return sa;
    return itemField(row, 'item_id'); // legacy/inline-def fallback
}
function hasStatus(caster: CachedCaster, statusItemId_: string): boolean {
    return statusRows(caster).some((st) => statusItemId(st) === statusItemId_);
}
/** Prefer a joined origin name; lean browser statuses resolve their origin through the shared catalog reader. */
function statusOriginName(effect: unknown): string {
    const record = effect && typeof effect === 'object' && !Array.isArray(effect)
        ? effect as Record<string, unknown>
        : {};
    const status = record.status_arr && typeof record.status_arr === 'object' && !Array.isArray(record.status_arr)
        ? record.status_arr as Record<string, unknown>
        : {};
    const raw = record.status_origin_id_item_name ?? status.status_origin_id_item_name;
    const v = String(unwrap(raw) ?? '').trim();
    if (v !== '') return v;
    const originId = String(unwrap(ftLayerValue(record, 'status_origin_id')) ?? '').trim();
    return (originId && typeof ftItemNameById === 'function' ? ftItemNameById(originId).trim() : '') || 'Unknown';
}

/** render_end_concentration_action_buttons twin. */
export function renderEndConcentrationButtons(
    caster: CachedCaster,
    withP1Wrapper = true,
    compactToolbar = false,
): string {
    const rows = statusRows(caster);
    if (rows.length === 0) return '';

    const btnClass = compactToolbar
        ? 'btn btn-info text-dark border d-inline-flex align-items-center justify-content-center py-2 px-3 lh-1'
        : 'btn btn-info text-dark';

    let html = '';
    for (const effect of rows) {
        if (statusItemId(effect) !== CONCENTRATING_ID) continue;
        const originName = escapeHtml(statusOriginName(effect));
        const effectRecord = effect && typeof effect === 'object' && !Array.isArray(effect)
            ? effect as Record<string, unknown>
            : {};
        const statusId = String(unwrap(effectRecord.status_id) ?? '');
        if (withP1Wrapper) html += '<div class="p-1">';
        html += `<button class="${escapeHtml(btnClass)}" type="button" onclick="handleuseAbilityVariable1BtnSingleClick('${END_CONCENTRATION_ABILITY}', '${escapeHtml(statusId)}');" data-ability_id="${END_CONCENTRATION_ABILITY}"><span class="d-inline-block" data-bs-toggle="tooltip" title="" data-bs-original-title="End Concentration: ${originName}"><img src="${CDN_ICONS}end_concentration.png" width="25" height="25" alt="End Concentration"></span></button>`;
        if (withP1Wrapper) html += '</div>';
    }
    return html;
}

/** render_caster_special_actions_column twin. Emits ONLY the inner ability-group (or '' when
 *  there is nothing to show). The grid column wrapper lives on the host element this is injected
 *  into (#ft_special_actions_*, which now carries the col-* class) — emitting our own col here
 *  double-nested a column inside a column-less host, so the strip dropped to its own row at the
 *  far left in 3-column view (Fortunate adrift below the grid). */
function renderCasterSpecialActions(caster: CachedCaster): string {
    const ecArr = ecArrOf(caster);
    // Fortunate SPENDS luck — require a positive pool. (The PHP original gated on truthiness,
    // which also passed NEGATIVE luck; owner-flagged 2026-06-10: -1 luck still showed the button.)
    const hasLuck = Number(unwrap(ecArr.luck) ?? 0) > 0;
    const isShapeshifted = !!unwrap(caster.shapeshifted);

    // Wrap only the applicable actions so an empty strip leaves no flex margin.
    let html = '';

    // Fortunate (Luck)
    if (hasLuck) {
        html += `<div class="p-1"><button class="btn btn-info text-dark" onclick="handleAbilityBtnSingleClick('${FORTUNATE_ABILITY}');" data-ability_id="${FORTUNATE_ABILITY}">${iconMap('fortunate')}</button></div>`;
    }

    html += renderEndConcentrationButtons(caster);

    // Per-status action buttons
    for (const effect of statusRows(caster)) {
        const id = statusItemId(effect);
        if (id === BURNING_ID) {
            html += `<div class="p-1"><button class="btn btn-info text-dark" onclick="handleAbilityBtnSingleClick('${STOP_BURNING_ABILITY}');" data-ability_id="${STOP_BURNING_ABILITY}">${iconMap('stop_burning')}</button></div>`;
        }
        if (id === HUNKERED_DOWN_ID) {
            html += `<div class="p-1"><button class="btn btn-info text-dark" onclick="handleAbilityBtnSingleClick('${END_HUNKER_ABILITY}');" data-ability_id="${END_HUNKER_ABILITY}">${iconMap('end_hunker_down')}</button></div>`;
        }
    }

    // Revert Shapeshift
    if (isShapeshifted) {
        html += `<div class="p-1"><button class="btn btn-info text-dark" onclick="handleAbilityBtnSingleClick('${REVERT_SHAPESHIFT_ABILITY}');" data-ability_id="${REVERT_SHAPESHIFT_ABILITY}">${iconMap('revert_shapeshift')}</button></div>`;
    }

    // Match COLUMN 1 / COLUMN 2's group exactly (…gap-1 mb-2) so the strip aligns flush with them.
    return html === ''
        ? ''
        : `<div class="ft-core-ability-group d-flex flex-wrap align-items-center gap-1 mb-2">${html}</div>`;
}

// ── Action-bar contextual-actions cluster (far right of the bar) ───────────────────────────────────
// More conditions than the special-actions column: it folds in stop-bleeding, grapple-regress, stand-up
// and dismount/stop-piloting (ids + gating mirror tabs/movement.ts + tabs/core-attacks.ts + card-shell.ts),
// alongside the existing luck/burning/cover/hunker/shapeshift/concentration set. Each button fires an
// existing ability via handleAbilityBtnSingleClick; only the APPLICABLE ones render, so it's contextual.
const STOP_BLEEDING_ABILITY = '904F852B-443B-24E4-7B71-F785BB66433B';
const BLEEDING_IDS = ['3BE713AB-37D7-630B-C1F8-9470784BA33E', '22E33A8E-DF5D-2596-8E2D-0E8A8528E47C', 'BAAA237B-1AA7-0409-5BC7-1A6720573069'];
const GRAPPLED_ID = '0C2A8037-B0BC-8300-C818-198881B00E7D';
const RESTRAINED_ID = 'E34AF232-5438-88FC-9A9F-73850CCCFD51';
const GRAPPLE_ESCAPE_ABILITY = '98BE66E8-2F3D-DFEF-B4CD-DBE1EAA9ED4D';
const GRAPPLE_RESTRAINED_BREAK_ABILITY = '2371CC7D-0BAD-DA78-7067-32361F5DDDBC';
const PRONE_ID = 'F5ACBDB4-D923-3684-57F6-FBF3F720AEFA';
const CRIPPLED_ID = '99C862B9-D6AE-4E18-7803-A6F9B42A88E1';
const IMMOBILE_ID = 'F40E19A3-FC86-187F-050E-8C8B69761353';
const STAND_UP_ABILITY = '21D8FBA4-AD2E-EA72-965D-E0445CBFF722';
const RIDING_ID = 'FF8D219A-2A83-0EE5-A222-E748753E8D0B';
const PILOTING_ID = 'A1F1FAF2-DA1E-D537-36C2-2B74DC82CB12';
const RIDDEN_ID = 'F13F6A58-FE91-F24D-12BE-BE7CCAD81A40';
const PILOTED_ID = 'DDC66C31-4093-84AF-18BB-093D924B8738';
const DISMOUNT_ABILITY = '69046DF3-CF81-45F6-BBC8-C237D2B38F5B';
const EXIT_VEHICLE_ABILITY = '40FECD83-9B94-96AB-4601-EBF9B081C7E3';
// Buck Riders — a creature Ridden/Piloted by a HOSTILE (opposing-team) rider contests to throw them off.
const BUCK_RIDERS_ABILITY = '4476C212-A776-CF84-67A6-5E79C77CB381';
/** True when THIS creature is Ridden or Piloted by a rider on an OPPOSING team — the trigger for the Buck
 *  Riders contextual action (a hostile forced-mounter can be thrown off; a WILLING same-team mount must NOT
 *  surface it). Rider = the Ridden/Piloted status row's status_caster_id; teams resolve from the encounterArr
 *  cache (the live WS truth) via window.teamFromCacheById + window.isOpposingTeamPair — mirrors the server
 *  check_opposing_team gate in _movement_inc.php. Fail-closed: no resolvable hostile rider → no button. */
function hasHostileRider(caster: CachedCaster): boolean {
    const myEcId = String(unwrap(ecArrOf(caster).encounter_creature_id) ?? '').trim();
    if (!myEcId) return false;
    const teamOf = window.teamFromCacheById;
    const opposing = window.isOpposingTeamPair;
    if (typeof teamOf !== 'function' || typeof opposing !== 'function') return false;
    const myTeam = teamOf(myEcId);
    for (const row of statusRows(caster)) {
        const sid = statusItemId(row);
        if (sid !== RIDDEN_ID && sid !== PILOTED_ID) continue;
        const rowRecord = row && typeof row === 'object' && !Array.isArray(row) ? row as Record<string, unknown> : {};
        const riderId = String(unwrap(rowRecord.status_caster_id) ?? '').trim();
        if (riderId === '') continue;
        if (opposing(myTeam, teamOf(riderId))) return true;
    }
    return false;
}

function ctxBtn(casterId: string, abilityId: string, icon: string, title: string): string {
    // iconMap()'s 4th arg = false → bare <img>, NO inner tooltip span. The BUTTON owns the single
    // tooltip; without this the icon's own tooltip stacks on the button's → a doubled bs-tooltip.
    return `<button type="button" class="ft-action-context-btn" data-bs-toggle="tooltip" data-bs-placement="top" data-bs-animation="false" data-bs-original-title="${escapeHtml(title)}" onclick="if(!(window.ftDispatchAbilityClick&amp;&amp;window.ftDispatchAbilityClick('${escapeHtml(casterId)}','${abilityId}')))handleAbilityBtnSingleClick('${abilityId}');" data-ability_id="${abilityId}">${iconMap(icon, 25, 25, false)}</button>`;
}

/** The far-right contextual-actions cluster for the action bar. '' when nothing applies. Sync. The
 *  action bar passes the exact snapshot it is painting so Stand Up / Escape / Dismount / Hunker / Revert
 *  and End Concentration derive from the SAME row as the rest of the bar; the casterCache read is only
 *  the fallback for an independent caller. Buttons are emitted in priority order; CSS lays them out
 *  right-to-left so the most-pressing action is rightmost (nearest the controls). */
export function renderActionBarContextualActions(casterId: string, casterSnapshot?: CachedCaster): string {
    const caster = casterSnapshot
        ?? (window.FateTide?.casterCache?.get(casterId) as CachedCaster | undefined);
    if (!caster) return '';
    const has = (id: string) => hasStatus(caster, id);
    const grappled = has(GRAPPLED_ID), restrained = has(RESTRAINED_ID), prone = has(PRONE_ID);
    const crippled = has(CRIPPLED_ID), immobile = has(IMMOBILE_ID);
    const hunkered = has(HUNKERED_DOWN_ID);

    const btns: string[] = [];
    if (latchedObject(caster)) btns.push(`<button type="button" class="ft-action-context-btn" onclick="if(window.ftLatchChoice)window.ftLatchChoice('${escapeHtml(casterId)}');else window.displayWarning?.('Latching is still loading. Try again shortly.');">Unlatch</button>`);
    // Fortunate is intentionally NOT here — the caster card's special-actions strip already shows it, and a
    // second copy in the bar's contextual cluster was a duplicate ("Use Fortunate" + "Fortunate"). (owner 2026-06-26)
    if (BLEEDING_IDS.some(has)) btns.push(ctxBtn(casterId, STOP_BLEEDING_ABILITY, 'stop_the_bleeding', 'Stop Bleeding'));
    if (has(BURNING_ID)) btns.push(ctxBtn(casterId, STOP_BURNING_ABILITY, 'stop_burning', 'Stop Burning'));
    if (grappled && !restrained) btns.push(ctxBtn(casterId, GRAPPLE_ESCAPE_ABILITY, 'regress_grapple_escape', 'Escape Grapple'));
    if (grappled && restrained) btns.push(ctxBtn(casterId, GRAPPLE_RESTRAINED_BREAK_ABILITY, 'regress_grapple_restrained', 'Break Free (Restrained)'));
    if (prone && !grappled && !restrained && !crippled && !immobile) btns.push(ctxBtn(casterId, STAND_UP_ABILITY, 'stand_up', 'Stand Up'));
    // Mount/pilot exit + Buck. Exactly one button, in priority order:
    //  • RIDDEN/PILOTED by a HOSTILE rider → Buck Riders (throw the forced-mounter off) — takes priority so a
    //    force-mounted creature gets the counter, not a "Dismount" it can't perform (it's the mount, not the rider).
    //  • Caster is the RIDER (Piloting) → Stop Piloting (Exit Vehicle); (Riding/willing Ridden) → Dismount.
    // (The exit abilities remove the same caster statuses server-side; the split is UX. Mirrors tabs/movement.ts
    // + _movement_inc.php.)
    if (!prone && !grappled && !restrained && !immobile && !crippled) {
        if (hasHostileRider(caster)) {
            btns.push(ctxBtn(casterId, BUCK_RIDERS_ABILITY, 'buck_creature', 'Buck Rider'));
        } else if (has(PILOTING_ID)) {
            btns.push(ctxBtn(casterId, EXIT_VEHICLE_ABILITY, 'exit_vehicle', 'Stop Piloting'));
        } else if (has(RIDING_ID)) {
            // Riding only — the RIDER dismounts. A Ridden creature is the MOUNT (its counter is Buck, above).
            btns.push(ctxBtn(casterId, DISMOUNT_ABILITY, 'dismount', 'Dismount'));
        }
    }
    if (hunkered) btns.push(ctxBtn(casterId, END_HUNKER_ABILITY, 'end_hunker_down', 'End Hunker Down'));
    if (unwrap(caster.shapeshifted)) btns.push(ctxBtn(casterId, REVERT_SHAPESHIFT_ABILITY, 'revert_shapeshift', 'Revert Shapeshift'));
    // End Concentration (one per concentrating status; flat, no p-1 wrapper).
    const endConc = renderEndConcentrationButtons(caster, false);

    const all = btns.join('') + endConc;
    return all === '' ? '' : `<div class="ft-action-context">${all}</div>`;
}

async function loadCaster(casterId: string): Promise<CachedCaster | undefined> {
    let caster = window.FateTide?.casterCache?.get(casterId) as CachedCaster | undefined;
    if (!caster) {
        try {
            await fetchAndCacheCaster(casterId);
        } catch {
            // fetchAndCacheCaster swallows its own errors; fall through to the miss guard.
        }
        caster = window.FateTide?.casterCache?.get(casterId) as CachedCaster | undefined;
    }
    return caster;
}

/** Inline entry: special-actions column (Luck / concentration / burning / cover / shapeshift). */
export async function renderCasterSpecialActionsInline(
    casterId: string,
    targetId: string,
    encounterCreatureView = false,
): Promise<void> {
    const target = document.getElementById(targetId);
    if (!target) return;
    const caster = await loadCaster(casterId);
    // The grid column class lives on `target` (#ft_special_actions_*); we inject only the group.
    paintMarkup(target, caster ? renderCasterSpecialActions(caster) : '');
}

/** Inline entry: standalone End Concentration buttons (castable-spells nav row uses compact). */
export async function renderEndConcentrationButtonsInline(
    casterId: string,
    targetId: string,
    withP1Wrapper = true,
    compactToolbar = false,
): Promise<void> {
    const target = document.getElementById(targetId);
    if (!target) return;
    const caster = await loadCaster(casterId);
    paintMarkup(target, caster ? renderEndConcentrationButtons(caster, !!withP1Wrapper, !!compactToolbar) : '');
}
